import { useMutation, useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { ContentHeader, Field, MoneyInput, QtyInput, QueryStatus, StatusBadge, useToast } from "@cx/ui";
import type { CompanyFiscalProfile, TaxOperationDetail, TaxSimulationResult } from "@cx/contracts";
import { can, type Permissions } from "@cx/db";
import { BRAZIL_UFS, EXTERIOR, IE_INDICATORS, formatBRL, isSimplesCrt, type IeIndicator } from "@cx/domain";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { TaxOperationDialog } from "./TaxOperationDialog";
import { EMPTY_RULE, TaxRuleDialog, ruleValues, type RuleValues } from "./TaxRuleDialog";
import { TaxTotalsList } from "./TaxEstimate";
import {
  DIRECTION_LABEL,
  DOC_MODEL_LABEL,
  IE_INDICATOR_LABEL,
  PRESENCE_LABEL,
  PURPOSE_LABEL,
  ruleConditionsText,
  ruleTaxText,
} from "./tax-labels";

/** Natureza de operação: dados, regras de tributação e simulador (T2.2). */
export function TaxOperationPage(): ReactElement {
  const { id } = useParams({ strict: false }) as { id: string };
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const canUpdate = can(perms, "tax_operations", "update");
  const q = useQuery({
    queryKey: ["tax-operations", companyId, "detail", id],
    queryFn: () => api<TaxOperationDetail>(`/tax-operations/${id}`),
  });
  const profile = useQuery({
    queryKey: ["fiscal-company", companyId],
    queryFn: () => api<CompanyFiscalProfile>("/fiscal/company"),
  });
  const [editing, setEditing] = useState(false);
  const [rule, setRule] = useState<{ id?: string; values: RuleValues } | null>(null);
  const o = q.data;
  const simples = profile.data ? isSimplesCrt(profile.data.crt) : false;
  return (
    <>
      <ContentHeader
        title={o?.name ?? "Natureza de operação"}
        breadcrumb={["Preferências", "Fiscal", "Naturezas de operação", o?.name ?? ""]}
        actions={
          o &&
          canUpdate && (
            <button type="button" className="btn btn-outline-primary" onClick={() => setEditing(true)}>
              Editar natureza
            </button>
          )
        }
      />
      <QueryStatus loading={q.isLoading} error={q.error} />
      {o && (
        <>
          <section className="box card mb-3" aria-labelledby="tax-op-data">
            <div className="card-body">
              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <h2 id="tax-op-data" className="h5 mb-0 me-auto">
                  Dados da natureza
                </h2>
                {o.isDefault && <StatusBadge tone="info">Padrão</StatusBadge>}
                <StatusBadge tone={o.active ? "success" : "secondary"}>
                  {o.active ? "Ativa" : "Inativa"}
                </StatusBadge>
              </div>
              <dl className="row mb-0">
                <dt className="col-sm-3">Tipo</dt>
                <dd className="col-sm-9">{DIRECTION_LABEL[o.direction]}</dd>
                <dt className="col-sm-3">Finalidade</dt>
                <dd className="col-sm-9">{PURPOSE_LABEL[o.purpose]}</dd>
                <dt className="col-sm-3">Documento</dt>
                <dd className="col-sm-9">{DOC_MODEL_LABEL[o.docModel]}</dd>
                <dt className="col-sm-3">Presença</dt>
                <dd className="col-sm-9">{PRESENCE_LABEL[o.presenceIndicator]}</dd>
                <dt className="col-sm-3">Movimentações</dt>
                <dd className="col-sm-9">
                  {[o.postStock && "Estoque", o.postFinancial && "Financeiro"].filter(Boolean).join(" e ") ||
                    "Nenhuma"}
                </dd>
                {o.additionalInfo && (
                  <>
                    <dt className="col-sm-3">Informações complementares</dt>
                    <dd className="col-sm-9">{o.additionalInfo}</dd>
                  </>
                )}
              </dl>
            </div>
          </section>
          <section className="box card mb-3" aria-labelledby="tax-op-rules">
            <div className="card-body">
              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <h2 id="tax-op-rules" className="h5 mb-0 me-auto">
                  Regras de tributação
                </h2>
                {canUpdate && (
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    onClick={() =>
                      setRule({ values: { ...EMPTY_RULE, cfop: o.direction === "out" ? "5102" : "1202" } })
                    }
                  >
                    Incluir regra
                  </button>
                )}
              </div>
              <p className="form-text mt-0">
                Para cada item vale a regra ativa mais específica (produto, NCM, UF, destinatário); a
                prioridade desempata. Campos vazios usam o padrão do motor, como a alíquota interna da UF.
              </p>
              {o.rules.length === 0 ? (
                <p className="text-muted mb-0">Nenhuma regra: sem regra a natureza não calcula impostos.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm align-middle mb-0" aria-label="Regras de tributação">
                    <thead>
                      <tr>
                        <th scope="col">Quando</th>
                        <th scope="col">Tributação</th>
                        <th scope="col" className="text-end">
                          Prioridade
                        </th>
                        <th scope="col">Situação</th>
                        <th scope="col">
                          <span className="visually-hidden">Ações</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {o.rules.map((r) => (
                        <tr key={r.id}>
                          <td>{ruleConditionsText(r)}</td>
                          <td>{ruleTaxText(r)}</td>
                          <td className="text-end">{r.priority}</td>
                          <td>
                            <StatusBadge tone={r.active ? "success" : "secondary"}>
                              {r.active ? "Ativa" : "Inativa"}
                            </StatusBadge>
                          </td>
                          <td className="text-end">
                            {canUpdate && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                aria-label={`Editar regra ${ruleTaxText(r)}`}
                                onClick={() => setRule({ id: r.id, values: ruleValues(r) })}
                              >
                                Editar
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>
          {o.direction === "out" && o.active && <SimulatorCard operationId={o.id} />}
          {editing && (
            <TaxOperationDialog operation={o} onClose={() => setEditing(false)} onSaved={() => undefined} />
          )}
          {rule && (
            <TaxRuleDialog
              operationId={o.id}
              ruleId={rule.id}
              initial={rule.values}
              simples={simples}
              onClose={() => setRule(null)}
            />
          )}
        </>
      )}
    </>
  );
}

type SimForm = {
  uf: string;
  ieIndicator: IeIndicator;
  finalConsumer: boolean;
  ncm: string;
  origin: number;
  qtyQ4: number | null;
  unitPriceCents: number | null;
  freightCents: number | null;
};

/** Simulador: um item avulso para conferir CFOP, CST e valores antes de usar a natureza. */
function SimulatorCard({ operationId }: { operationId: string }): ReactElement {
  const { api } = useSession();
  const toast = useToast();
  const [f, setF] = useState<SimForm>({
    uf: "",
    ieIndicator: 9,
    finalConsumer: true,
    ncm: "",
    origin: 0,
    qtyQ4: 10_000,
    unitPriceCents: null,
    freightCents: null,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const run = useMutation({
    mutationFn: () =>
      api<TaxSimulationResult>("/tax/simulate", {
        method: "POST",
        body: JSON.stringify({
          taxOperationId: operationId,
          recipient: { uf: f.uf, ieIndicator: f.ieIndicator, finalConsumer: f.finalConsumer },
          items: [
            {
              productId: null,
              ncm: f.ncm,
              origin: f.origin,
              qtyQ4: f.qtyQ4,
              unitPriceCents: f.unitPriceCents,
            },
          ],
          freightCents: f.freightCents ?? 0,
        }),
      }),
    onSuccess: () => setErrors({}),
    onError: (e) => {
      const fields = e instanceof ApiFailure && e.fields ? e.fields : {};
      setErrors({
        uf: fields["recipient.uf"] ?? fields.contactId ?? "",
        ncm: fields["items.0.ncm"] ?? "",
        qtyQ4: fields["items.0.qtyQ4"] ?? "",
        unitPriceCents: fields["items.0.unitPriceCents"] ?? "",
      });
      if (!(e instanceof ApiFailure && e.fields)) toast("danger", errorMessage(e));
    },
  });
  const item = run.data?.items[0];
  const err = (k: string) => errors[k] || undefined;
  return (
    <section className="box card mb-3" aria-labelledby="tax-sim">
      <div className="card-body">
        <h2 id="tax-sim" className="h5">
          Simular impostos
        </h2>
        <form
          className="row align-items-end"
          onSubmit={(e) => {
            e.preventDefault();
            run.mutate();
          }}
        >
          <Field label="UF do destinatário" required className="col-md-2" error={err("uf")}>
            <select className="form-select" value={f.uf} onChange={(e) => setF({ ...f, uf: e.target.value })}>
              <option value="">Selecione</option>
              {BRAZIL_UFS.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
              <option value={EXTERIOR}>Exterior</option>
            </select>
          </Field>
          <Field label="Destinatário" className="col-md-3">
            <select
              className="form-select"
              value={f.ieIndicator}
              onChange={(e) => {
                const ie = Number(e.target.value) as IeIndicator;
                setF({ ...f, ieIndicator: ie, finalConsumer: ie !== 1 });
              }}
            >
              {IE_INDICATORS.map((i) => (
                <option key={i} value={i}>
                  {IE_INDICATOR_LABEL[i]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="NCM" required className="col-md-2" error={err("ncm")}>
            <input
              value={f.ncm}
              maxLength={10}
              inputMode="numeric"
              onChange={(e) => setF({ ...f, ncm: e.target.value })}
            />
          </Field>
          <Field label="Origem" className="col-md-1">
            <select
              className="form-select"
              value={f.origin}
              onChange={(e) => setF({ ...f, origin: Number(e.target.value) })}
            >
              {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((x) => (
                <option key={x} value={x}>
                  {x}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Quantidade" required className="col-md-2" error={err("qtyQ4")}>
            <QtyInput value={f.qtyQ4} onChange={(v) => setF({ ...f, qtyQ4: v })} />
          </Field>
          <Field label="Preço unitário" required className="col-md-2" error={err("unitPriceCents")}>
            <MoneyInput value={f.unitPriceCents} onChange={(v) => setF({ ...f, unitPriceCents: v })} />
          </Field>
          <Field label="Frete" className="col-md-2">
            <MoneyInput value={f.freightCents} onChange={(v) => setF({ ...f, freightCents: v })} />
          </Field>
          <div className="col-md-3 mb-3">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="sim-final"
                checked={f.finalConsumer}
                onChange={(e) => setF({ ...f, finalConsumer: e.target.checked })}
              />
              <label className="form-check-label" htmlFor="sim-final">
                Consumidor final
              </label>
            </div>
          </div>
          <div className="col-md-3 mb-3">
            <button type="submit" className="btn btn-outline-primary" disabled={run.isPending}>
              Simular
            </button>
          </div>
        </form>
        {item && run.data && (
          <div className="row" aria-live="polite" data-testid="tax-sim-result">
            <div className="col-md-6">
              <dl className="row mb-0 small">
                <dt className="col-6 fw-normal">CFOP</dt>
                <dd className="col-6 mb-1">{item.cfop}</dd>
                <dt className="col-6 fw-normal">{item.icms.csosn ? "CSOSN" : "CST do ICMS"}</dt>
                <dd className="col-6 mb-1">{item.icms.csosn ?? item.icms.cst}</dd>
                <dt className="col-6 fw-normal">Valor dos produtos</dt>
                <dd className="col-6 mb-1">{formatBRL(item.grossCents)}</dd>
                {item.warnings.map((w) => (
                  <dd key={w} className="col-12 text-warning-emphasis mb-1">
                    {w}
                  </dd>
                ))}
              </dl>
            </div>
            <div className="col-md-6">
              <TaxTotalsList result={run.data} />
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
